import {
    ArrowRight,
    Download,
    Inbox,
    MessageSquareMore,
    Plus,
    Search,
    Truck,
    Upload,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DispatchFormDialog } from './DispatchFormDialog';
import { EnquiriesDialog } from './EnquiriesDialog';
import { TransportersDialog } from './TransportersDialog';
import {
    ACTIVE_STATUSES,
    STATUS_LABEL,
    backupAll,
    downloadFile,
    dueAmount,
    fetchCompanies,
    fetchDispatches,
    fetchDocCounts,
    formatINR,
    restoreAll,
    shortId,
    type Dispatch,
    type DispatchStatus,
} from './dispatchApi';

const PAGE = 48;

const selectCls =
    'h-10 w-full rounded-md border border-border bg-card px-3 text-xs font-medium text-foreground outline-none transition-colors hover:border-primary/50 focus-visible:ring-1 focus-visible:ring-primary focus-visible:border-primary';

const statusBadgeConfig: Record<
    DispatchStatus,
    { bg: string; text: string; border: string }
> = {
    delivered: {
        bg: 'bg-emerald-500/10',
        text: 'text-emerald-700 dark:text-emerald-300',
        border: 'border-emerald-600/30',
    },
    dispatched: {
        bg: 'bg-sky-500/10',
        text: 'text-sky-700 dark:text-sky-300',
        border: 'border-sky-600/30',
    },
    loading_done: {
        bg: 'bg-teal-500/10',
        text: 'text-teal-700 dark:text-teal-300',
        border: 'border-teal-600/30',
    },
    loading: {
        bg: 'bg-amber-500/10',
        text: 'text-amber-700 dark:text-amber-300',
        border: 'border-amber-600/30',
    },
    planned: {
        bg: 'bg-blue-500/10',
        text: 'text-blue-700 dark:text-blue-300',
        border: 'border-blue-600/30',
    },
    dc_created: {
        bg: 'bg-purple-500/10',
        text: 'text-purple-700 dark:text-purple-300',
        border: 'border-purple-600/30',
    },
    cancelled: {
        bg: 'bg-muted',
        text: 'text-muted-foreground',
        border: 'border-border',
    },
};

export const DispatchOrderList = () => {
    const [rows, setRows] = useState<Dispatch[]>([]);
    const [docCounts, setDocCounts] = useState<Record<string, number>>({});
    const [companies, setCompanies] = useState<Record<number, string>>({});
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState('');
    const [statusF, setStatusF] = useState('all');
    const [payF, setPayF] = useState('all');
    const [shown, setShown] = useState(PAGE);

    const [enqOpen, setEnqOpen] = useState(false);
    const [trOpen, setTrOpen] = useState(false);
    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState<Dispatch | null>(null);
    const [prefill, setPrefill] = useState<Partial<Dispatch> | null>(null);
    const restoreRef = useRef<HTMLInputElement>(null);

    const load = useCallback(async () => {
        try {
            const [d, c, comp] = await Promise.all([
                fetchDispatches(),
                fetchDocCounts(),
                fetchCompanies(),
            ]);
            setRows(d);
            setDocCounts(c);
            setCompanies(Object.fromEntries(comp.map(x => [x.id, x.name])));
        } catch (e: any) {
            toast.error(e.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const filtered = useMemo(() => {
        const s = q.trim().toLowerCase();
        return rows.filter(r => {
            if (statusF !== 'all' && r.status !== statusF) return false;
            if (payF === 'paid' && r.payment_status !== 'paid') return false;
            if (payF === 'due' && !(dueAmount(r) > 0)) return false;
            if (!s) return true;
            return [
                r.dispatch_no,
                r.vehicle_no,
                r.transporter_name,
                r.source_location,
                r.destination,
                r.notes,
                r.company_id ? companies[r.company_id] : '',
                shortId(r),
            ]
                .filter(Boolean)
                .some(v => String(v).toLowerCase().includes(s));
        });
    }, [rows, q, statusF, payF, companies]);

    useEffect(() => setShown(PAGE), [q, statusF, payF]);

    const kpi = useMemo(
        () => ({
            total: rows.length,
            active: rows.filter(r => ACTIVE_STATUSES.includes(r.status)).length,
            tonnage: rows.reduce((a, r) => a + (r.tonnage ?? 0), 0),
            freight: rows.reduce(
                (a, r) =>
                    a + (r.status === 'cancelled' ? 0 : r.freight_cost ?? 0),
                0,
            ),
            pending: rows.reduce((a, r) => a + dueAmount(r), 0),
        }),
        [rows],
    );

    const exportCsv = () => {
        const head = [
            'Dispatch No',
            'Date',
            'Customer',
            'From',
            'To',
            'Transporter',
            'Vehicle',
            'Tonnage (MT)',
            'Freight',
            'Paid',
            'Payment',
            'Status',
        ];
        const esc = (v: any) => `"${String(v ?? '').replace(/"/g, '""')}"`;
        const lines = filtered.map(r =>
            [
                r.dispatch_no,
                r.dispatch_date,
                r.company_id ? companies[r.company_id] : '',
                r.source_location,
                r.destination,
                r.transporter_name,
                r.vehicle_no,
                r.tonnage,
                r.freight_cost,
                r.paid_amount,
                r.payment_status,
                r.status,
            ]
                .map(esc)
                .join(','),
        );
        downloadFile(
            `consignments-${new Date().toISOString().slice(0, 10)}.csv`,
            [head.map(esc).join(','), ...lines].join('\n'),
            'text/csv',
        );
    };

    const backup = async () => {
        try {
            const data = await backupAll();
            downloadFile(
                `dispatch-backup-${new Date().toISOString().slice(0, 10)}.json`,
                JSON.stringify(data, null, 2),
                'application/json',
            );
            toast.success('Backup downloaded');
        } catch (e: any) {
            toast.error(e.message);
        }
    };

    const restore = async (file?: File) => {
        if (!file) return;
        if (
            !window.confirm(
                'Restore from backup? Existing records with the same IDs will be overwritten.',
            )
        )
            return;
        try {
            await restoreAll(JSON.parse(await file.text()));
            toast.success('Backup restored');
            load();
        } catch (e: any) {
            toast.error(`Restore failed: ${e.message}`);
        }
    };

    const openNew = (initial: Partial<Dispatch> | null = null) => {
        setEditing(null);
        setPrefill(initial);
        setFormOpen(true);
    };

    return (
        <div className="space-y-4 pb-16">
            {/* Top Navigation Bar: matches reference screenshot */}
            <div className="flex flex-col gap-3 rounded-lg border border-border bg-card p-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-3">
                    <div className="flex size-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        <Truck className="size-5" />
                    </div>
                    <div className="flex items-center gap-2">
                        <h1 className="text-xl font-bold tracking-tight text-foreground">
                            Consignment Register
                        </h1>
                        <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-bold tracking-wider text-emerald-600 dark:text-emerald-400 uppercase">
                            <span className="size-1.5 rounded-full bg-emerald-500 animate-pulse" />
                            TEAM SYNCED — LIVE
                        </span>
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-1.5">
                    <Button
                        id="open-enquiries-btn"
                        variant="outline"
                        size="sm"
                        className="h-8 border-border text-xs font-medium hover:bg-accent/40"
                        onClick={() => setEnqOpen(true)}
                    >
                        <MessageSquareMore className="size-3.5 mr-1" /> Enquiries
                    </Button>
                    <Button
                        id="open-transporters-btn"
                        variant="outline"
                        size="sm"
                        className="h-8 border-border text-xs font-medium hover:bg-accent/40"
                        onClick={() => setTrOpen(true)}
                    >
                        <Truck className="size-3.5 mr-1" /> Transporters
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        className="h-8 border-border text-xs font-medium hover:bg-accent/40"
                        onClick={exportCsv}
                    >
                        <Download className="size-3.5 mr-1" /> Export CSV
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        className="h-8 border-border text-xs font-medium hover:bg-accent/40"
                        onClick={backup}
                    >
                        Backup
                    </Button>
                    <input
                        ref={restoreRef}
                        type="file"
                        accept="application/json"
                        className="hidden"
                        onChange={e => {
                            restore(e.target.files?.[0]);
                            e.target.value = '';
                        }}
                    />
                    <Button
                        variant="outline"
                        size="sm"
                        className="h-8 border-border text-xs font-medium hover:bg-accent/40"
                        onClick={() => restoreRef.current?.click()}
                    >
                        <Upload className="size-3.5 mr-1" /> Restore
                    </Button>
                    <Button
                        id="new-entry-btn"
                        size="sm"
                        className="h-8 bg-primary text-primary-foreground font-semibold text-xs hover:bg-primary/90"
                        onClick={() => openNew()}
                    >
                        <Plus className="size-3.5 mr-1" /> New Entry
                    </Button>
                </div>
            </div>

            {/* KPI Strip: Clean Horizontal Bar with Dividers */}
            <div className="grid grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-border rounded-lg border border-border bg-card p-4 sm:grid-cols-5 text-left">
                <div className="px-3 py-1">
                    <div className="text-2xl font-bold tracking-tight text-foreground">
                        {kpi.total}
                    </div>
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mt-0.5">
                        TOTAL ENTRIES
                    </div>
                </div>
                <div className="px-3 py-1">
                    <div className="text-2xl font-bold tracking-tight text-foreground">
                        {kpi.active}
                    </div>
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mt-0.5">
                        ACTIVE SHIPMENTS
                    </div>
                </div>
                <div className="px-3 py-1">
                    <div className="text-2xl font-bold tracking-tight text-foreground">
                        {kpi.tonnage.toLocaleString('en-IN', {
                            maximumFractionDigits: 2,
                        })}
                    </div>
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mt-0.5">
                        TOTAL TONNAGE (MT)
                    </div>
                </div>
                <div className="px-3 py-1">
                    <div className="text-2xl font-bold tracking-tight text-foreground">
                        {formatINR(kpi.freight)}
                    </div>
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mt-0.5">
                        TOTAL FREIGHT COST
                    </div>
                </div>
                <div className="px-3 py-1">
                    <div className="text-2xl font-bold tracking-tight text-amber-600 dark:text-amber-400">
                        {formatINR(kpi.pending)}
                    </div>
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mt-0.5">
                        PENDING PAYMENTS
                    </div>
                </div>
            </div>

            {/* Filter Section: 3 Clean Full-Width Stacked Inputs matching Reference UI */}
            <div className="space-y-2">
                <div className="relative">
                    <Search className="absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        id="dispatch-search"
                        className="h-10 rounded-md border-border bg-card pl-10 text-xs text-foreground placeholder:text-muted-foreground"
                        placeholder="Search vehicle no., transporter, driver, route, customer, ID…"
                        value={q}
                        onChange={e => setQ(e.target.value)}
                    />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <select
                        className={selectCls}
                        value={statusF}
                        onChange={e => setStatusF(e.target.value)}
                        id="dispatch-status-filter"
                    >
                        <option value="all">All statuses</option>
                        {Object.entries(STATUS_LABEL).map(([k, v]) => (
                            <option key={k} value={k}>
                                {v}
                            </option>
                        ))}
                    </select>
                    <select
                        className={selectCls}
                        value={payF}
                        onChange={e => setPayF(e.target.value)}
                        id="dispatch-payment-filter"
                    >
                        <option value="all">All payments</option>
                        <option value="paid">Paid</option>
                        <option value="due">Payment due</option>
                    </select>
                </div>
            </div>

            {/* Consignment Horizontal Cards List */}
            {loading ? (
                <div className="space-y-2">
                    {Array.from({ length: 5 }).map((_, i) => (
                        <div
                            key={i}
                            className="h-20 animate-pulse rounded-lg border border-border bg-card/60"
                        />
                    ))}
                </div>
            ) : filtered.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border py-16 text-center text-muted-foreground">
                    <Inbox className="size-8" />
                    <p className="text-sm font-medium">No consignments found</p>
                    <p className="text-xs">Try adjusting your search query or status filter.</p>
                </div>
            ) : (
                <div className="space-y-2.5">
                    {filtered.slice(0, shown).map(r => {
                        const due = dueAmount(r);
                        const docs =
                            (docCounts[r.id] ?? 0) +
                            (r.delivery_challan_id ? 1 : 0);
                        const paid =
                            r.payment_status === 'paid' ||
                            (r.freight_cost ?? 0) === 0;
                        const st =
                            statusBadgeConfig[r.status] ||
                            statusBadgeConfig.planned;

                        return (
                            <div
                                key={r.id}
                                role="button"
                                tabIndex={0}
                                onClick={() => {
                                    setEditing(r);
                                    setPrefill(null);
                                    setFormOpen(true);
                                }}
                                onKeyDown={e => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        setEditing(r);
                                        setPrefill(null);
                                        setFormOpen(true);
                                    }
                                }}
                                className="group relative flex flex-col md:flex-row md:items-center justify-between gap-3.5 rounded-lg border border-border bg-card p-3.5 sm:px-5 sm:py-3.5 text-left transition-all hover:border-primary/70 hover:bg-primary/[0.02] cursor-pointer"
                            >
                                {/* Left Solid Accent Bar */}
                                <div className="absolute inset-y-0 left-0 w-1.5 rounded-l-lg bg-primary" />

                                {/* Section 1: ID, Date & DC Linked */}
                                <div className="w-full md:w-36 shrink-0 pl-1.5">
                                    <div className="font-mono text-xs font-bold text-foreground">
                                        #{shortId(r)}
                                    </div>
                                    <div className="mt-0.5 text-xs text-muted-foreground">
                                        {r.dispatch_date}
                                    </div>
                                    {r.delivery_challan_id && (
                                        <div className="mt-0.5">
                                            <span className="inline-block rounded bg-primary/10 px-1.5 py-0.2 text-[10px] font-semibold text-primary">
                                                DC Linked
                                            </span>
                                        </div>
                                    )}
                                </div>

                                {/* Section 2: Route, Transporter & Customer */}
                                <div className="flex-1 min-w-0 pr-2">
                                    <div className="flex items-center gap-2 text-sm sm:text-base font-semibold text-foreground">
                                        <span className="truncate max-w-[200px] sm:max-w-[260px]">
                                            {r.source_location || 'Source'}
                                        </span>
                                        <ArrowRight className="size-4 shrink-0 text-primary" />
                                        <span className="truncate max-w-[200px] sm:max-w-[260px]">
                                            {r.destination || 'Destination'}
                                        </span>
                                    </div>
                                    <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                                        <span className="font-medium text-foreground/80">
                                            {r.transporter_name || 'No transporter'}
                                        </span>
                                        {r.vehicle_no && (
                                            <>
                                                <span>•</span>
                                                <span className="font-mono font-medium text-foreground">
                                                    {r.vehicle_no}
                                                </span>
                                            </>
                                        )}
                                        {r.company_id && companies[r.company_id] && (
                                            <>
                                                <span>•</span>
                                                <span className="truncate text-foreground/60 max-w-[220px]">
                                                    {companies[r.company_id]}
                                                </span>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {/* Section 3: Metrics (Tonnage, Length, Cost) */}
                                <div className="flex items-center justify-between md:justify-end gap-6 sm:gap-8 shrink-0 py-1 md:py-0 border-t md:border-t-0 border-border/50">
                                    <div className="text-left md:text-right">
                                        <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                                            TONNAGE
                                        </div>
                                        <div className="mt-0.5 text-sm font-bold tabular-nums text-foreground">
                                            {r.tonnage != null ? `${r.tonnage} MT` : '—'}
                                        </div>
                                    </div>
                                    <div className="text-left md:text-right">
                                        <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                                            LENGTH
                                        </div>
                                        <div className="mt-0.5 text-sm font-bold tabular-nums text-foreground">
                                            {r.vehicle_length_ft != null
                                                ? `${r.vehicle_length_ft} ft`
                                                : '—'}
                                        </div>
                                    </div>
                                    <div className="text-left md:text-right min-w-[75px]">
                                        <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                                            COST
                                        </div>
                                        <div className="mt-0.5 text-sm font-bold tabular-nums text-foreground">
                                            {r.freight_cost
                                                ? formatINR(r.freight_cost)
                                                : '—'}
                                        </div>
                                    </div>
                                </div>

                                {/* Section 4: Status Badge, Payment Pill & Docs */}
                                <div className="flex flex-row md:flex-col items-center md:items-end justify-between md:justify-center gap-1.5 shrink-0 md:min-w-[110px] pt-1 md:pt-0 border-t md:border-t-0 border-border/50">
                                    {/* Clean Status Badge */}
                                    <span
                                        className={`inline-flex items-center rounded border px-2 py-0.5 text-[11px] font-bold tracking-wider uppercase ${st.bg} ${st.text} ${st.border}`}
                                    >
                                        {STATUS_LABEL[r.status]}
                                    </span>

                                    {/* Payment Badge */}
                                    {(r.freight_cost ?? 0) > 0 ? (
                                        <span
                                            className={`rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                                                paid
                                                    ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30'
                                                    : 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-500/30'
                                            }`}
                                        >
                                            {paid ? 'PAID' : due > 0 ? `DUE: ${formatINR(due)}` : 'PAYMENT DUE'}
                                        </span>
                                    ) : null}

                                    {/* Docs Count */}
                                    <span className="text-[11px] text-muted-foreground">
                                        {docs} {docs === 1 ? 'doc' : 'docs'}
                                    </span>
                                </div>
                            </div>
                        );
                    })}

                    {shown < filtered.length && (
                        <div className="flex justify-center pt-3">
                            <Button
                                variant="outline"
                                size="sm"
                                className="h-8 border-border text-xs font-medium"
                                onClick={() => setShown(s => s + PAGE)}
                            >
                                Show more ({filtered.length - shown} remaining)
                            </Button>
                        </div>
                    )}
                </div>
            )}

            {/* Modals & Dialogs */}
            <EnquiriesDialog
                open={enqOpen}
                onOpenChange={setEnqOpen}
                onBook={(enq, t, quote) => {
                    setEnqOpen(false);
                    openNew({
                        enquiry_id: enq.id,
                        dispatch_date: enq.enquiry_date,
                        source_location: enq.loading_location,
                        destination: enq.unloading_location,
                        tonnage: enq.tonnage,
                        vehicle_length_ft: enq.vehicle_length_ft,
                        transporter_id: t.id,
                        transporter_name: t.name,
                        freight_cost: quote,
                        notes: enq.description,
                    });
                }}
            />
            <TransportersDialog open={trOpen} onOpenChange={setTrOpen} />
            <DispatchFormDialog
                open={formOpen}
                onOpenChange={setFormOpen}
                record={editing}
                initial={prefill}
                onSaved={load}
            />
        </div>
    );
};